import { useState } from 'react';
import './OzonMetricPanels.css';

// Показатели друг под другом на одной оси: у каждого своя шкала, у всех общая подсказка по моменту.
// Пропуск в линии — нет данных, а не ноль. Используется и «Днём группы по получасам», и динамикой по дням,
// и графиком «Статистики».
const fmt = value => (value == null ? 'нет данных'
  : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 }));

export function rolling(values, window = 7) {
  return values.map((_, index) => {
    const part = values.slice(Math.max(0, index - window + 1), index + 1).filter(value => value != null);
    return part.length ? part.reduce((sum, value) => sum + value, 0) / part.length : null;
  });
}

/**
 * Один график на все выбранные показатели: у показателей разные единицы (шт., ₽, %), поэтому каждая линия растянута на
 * высоту графика по своему размаху. Показатели с одним `group` делят шкалу (клики, «обычно» и цель сравнимы между
 * собой). Настоящие значения момента — под графиком и в подсказке.
 * points: [{ id, label, at?: 0..1, <key>: number|null }]
 * series: [{ key, label, unit, color, group?, dashed?: '4 3' }]
 * marks: [{ at, up, title, className }] — поправки тактики треугольниками внизу; nowAt — вертикаль «сейчас»;
 * ticks: [{ at, label }] — подписи оси.
 */
export function MetricChart({ points, series, title, ticks, marks, nowAt, left = 12, right = 12, width = 960,
  axes, showTicks = true, height, hoverIndex, onHover, heading }) {
  const [localHover, setLocalHover] = useState(null);
  // Две половины графика делят одну подсказку: наведение управляется снаружи, если передан onHover.
  const raw = onHover ? hoverIndex : localHover;
  const hover = raw != null && raw < (points?.length || 0) ? raw : null;
  const setHover = onHover || setLocalHover;
  if (!points?.length || !series?.length) return null;
  const W = width, H = height || 280, TOP = 12, BOTTOM = showTicks ? 30 : 8;
  const plotH = H - TOP - BOTTOM;
  const xAt = fraction => left + fraction * (W - left - right);
  const x = (point, index) => xAt(point.at != null ? point.at : index / Math.max(1, points.length - 1));
  const range = {};
  series.forEach(item => {
    const group = item.group || item.key;
    points.forEach(point => {
      const value = point[item.key];
      if (value == null) return;
      const current = range[group] || { low: value, high: value };
      range[group] = { low: Math.min(current.low, value), high: Math.max(current.high, value) };
    });
  });
  const yOf = item => {
    const { low = 0, high = 1 } = range[item.group || item.key] || {};
    return value => TOP + plotH - ((value - low) / Math.max(1e-9, high - low)) * plotH;
  };
  const withData = points.filter(point => series.some(item => point[item.key] != null));
  const picked = hover == null ? withData[withData.length - 1] : points[hover];
  const axis = ticks || [0, 0.5, 1].map(fraction => ({ at: fraction, label: points[Math.round(fraction * (points.length - 1))].label }));
  // Цветные шкалы по краям: слева и справа подписаны размах и название показателя, чья линия этого цвета.
  const axisItem = side => (axes && axes[side] ? series.find(item => item.key === axes[side]) : null);
  const edge = side => {
    const item = axisItem(side);
    const span = item ? range[item.group || item.key] : null;
    return item && span ? { item, span } : null;
  };
  return <div className={`mc${axes ? ' mc--half' : ''}`} aria-label={title || 'График показателей'}>
    {(heading || axes) && <div className='mc__axes'>
      <span className='mc__axis-left' style={{ color: edge('left')?.item.color }}>{edge('left') ? `← ${edge('left').item.label}, ${edge('left').item.unit}` : ''}</span>
      {heading && <strong>{heading}</strong>}
      <span className='mc__axis-right' style={{ color: edge('right')?.item.color }}>{edge('right') ? `${edge('right').item.label}, ${edge('right').item.unit} →` : ''}</span>
    </div>}
    <svg viewBox={`0 0 ${W} ${H}`} role='img' aria-label='График выбранных показателей'>
      {[0, .5, 1].map(f => <line key={f} x1={left} x2={W - right} y1={TOP + plotH * f} y2={TOP + plotH * f} stroke='currentColor' opacity='0.1' />)}
      {['left', 'right'].map(side => {
        const found = edge(side);
        if (!found) return null;
        const px = side === 'left' ? left - 6 : W - right + 6;
        const anchor = side === 'left' ? 'end' : 'start';
        return <g key={side} data-axis={side}>
          <text x={px} y={TOP + 10} textAnchor={anchor} fontSize='11' fill={found.item.color}>{fmt(found.span.high)}</text>
          <text x={px} y={TOP + plotH * 0.5 + 4} textAnchor={anchor} fontSize='11' fill={found.item.color} opacity='0.7'>{fmt((found.span.high + found.span.low) / 2)}</text>
          <text x={px} y={TOP + plotH} textAnchor={anchor} fontSize='11' fill={found.item.color}>{fmt(found.span.low)}</text>
        </g>;
      })}
      {showTicks && axis.map(tick => <g key={`${tick.at}${tick.label}`}>
        <line x1={xAt(tick.at)} x2={xAt(tick.at)} y1={TOP} y2={TOP + plotH} stroke='currentColor' opacity='0.06' />
        <text x={xAt(tick.at)} y={H - 10} textAnchor={tick.at === 0 ? 'start' : tick.at === 1 ? 'end' : 'middle'} fontSize='12' fill='currentColor' opacity='0.75'>{tick.label}</text>
      </g>)}
      {nowAt != null && <line x1={xAt(nowAt)} x2={xAt(nowAt)} y1={TOP} y2={TOP + plotH} stroke='#64748b' strokeDasharray='1 3' />}
      {series.map(item => {
        const y = yOf(item);
        const parts = [];
        let current = [];
        points.forEach((point, index) => {
          if (point[item.key] == null) { if (current.length) parts.push(current); current = []; return; }
          current.push(`${x(point, index).toFixed(1)},${y(point[item.key]).toFixed(1)}`);
        });
        if (current.length) parts.push(current);
        return <g key={item.key} data-series={item.key}>
          {parts.map((part, index) => <polyline key={index} points={part.join(' ')} fill='none' stroke={item.color}
            strokeWidth={item.dashed ? 1.6 : 2.2} strokeDasharray={item.dashed || undefined} opacity={item.dashed ? 0.85 : 1} />)}
          {parts.filter(part => part.length === 1).map((part, index) => {
            const [px, py] = part[0].split(',');
            return <circle key={`d${index}`} cx={px} cy={py} r='3' fill={item.color} />;
          })}
        </g>;
      })}
      {(marks || []).map((mark, index) => {
        const mx = xAt(mark.at), my = TOP + plotH - 8;
        const path = mark.up ? `M${mx - 5},${my + 5} L${mx + 5},${my + 5} L${mx},${my - 4} Z` : `M${mx - 5},${my - 4} L${mx + 5},${my - 4} L${mx},${my + 5} Z`;
        return <path key={index} d={path} className={mark.className}><title>{mark.title}</title></path>;
      })}
      {hover != null && <line x1={x(points[hover], hover)} x2={x(points[hover], hover)} y1={TOP} y2={TOP + plotH} stroke='currentColor' opacity='0.35' />}
      {points.map((point, index) => <rect key={point.id} x={x(point, index) - (W - left - right) / points.length / 2} y={TOP}
        width={(W - left - right) / points.length} height={plotH} fill='transparent' tabIndex='0'
        onMouseEnter={() => setHover(index)} onFocus={() => setHover(index)}
        aria-label={`${point.label}: ${series.map(item => `${item.label} ${fmt(point[item.key])}`).join('; ')}`} />)}
    </svg>
    <ul className='mc__legend' aria-label='Значения момента'>
      <li className='mc__moment'><strong>{picked?.label}</strong></li>
      {series.map(item => <li key={item.key}>
        <i style={{ background: item.dashed ? 'transparent' : item.color, borderTop: item.dashed ? `2px dashed ${item.color}` : undefined }} />
        {item.label}: <b>{fmt(picked?.[item.key])}{picked?.[item.key] != null ? ` ${item.unit}` : ''}</b></li>)}
    </ul>
    <small className='mc__note'>У каждого показателя своя шкала (от его минимума до максимума за период), чтобы все линии были видны; точные значения момента — под графиком.</small>
  </div>;
}

/**
 * Панель одного графика: галочки показателей над ним, выбор помнится в браузере (удобство, не состояние).
 * defaultOn — что включено, пока пользователь ничего не выбирал.
 */
export function MetricChartPanel({ id, points, series, defaultOn, ticks, marks, nowAt, footer, left, right, width }) {
  const key = `oz-auto-chart-${id}`;
  const [chosen, setChosen] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (Array.isArray(saved)) return saved.filter(item => series.some(s => s.key === item));
    } catch { /* per-viewer convenience only */ }
    return defaultOn;
  });
  const hasData = item => points.some(point => point[item.key] != null);
  const toggle = item => setChosen(current => {
    const next = current.includes(item.key) ? current.filter(k => k !== item.key) : [...current, item.key];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* per-viewer convenience only */ }
    return next;
  });
  const shown = series.filter(item => chosen.includes(item.key) && hasData(item));
  return <div className='mcp'>
    <fieldset className='mcp__choice'>
      <legend>Показатели на графике</legend>
      {series.map(item => <label key={item.key} className={hasData(item) ? '' : 'is-empty'}>
        <input type='checkbox' checked={chosen.includes(item.key)} disabled={!hasData(item)} onChange={() => toggle(item)} />
        <i style={{ background: item.dashed ? 'transparent' : item.color, borderTop: item.dashed ? `2px dashed ${item.color}` : undefined }} />
        {item.label}{hasData(item) ? '' : ' — нет данных'}</label>)}
    </fieldset>
    {shown.length === 0
      ? <p className='mc__note'>Отметьте хотя бы один показатель.</p>
      : <MetricChart points={points} series={shown} ticks={ticks} marks={marks} nowAt={nowAt} left={left} right={right} width={width} />}
    {footer && <p className='mc__note'>{footer}</p>}
  </div>;
}

/**
 * График из двух горизонтальных половин: сверху финансовые показатели, снизу рекламные. У каждой половины свои
 * галочки (две строки), свой ряд цветных шкал слева и справа и своя подсказка, а наведение общее: колонка дня
 * подсвечивается в обеих половинах. Шкалы слева и справа берутся у первого отмеченного показателя с `side`.
 * halves: [{ key, title, series: [{ key, label, unit, color, side?: 'left'|'right', group?, dashed? }] }]
 */
export function MetricHalves({ id, points, halves, defaultOn, ticks, footer }) {
  const key = `oz-auto-halves-${id}`;
  const all = halves.flatMap(half => half.series);
  const [chosen, setChosen] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (Array.isArray(saved)) return saved.filter(item => all.some(s => s.key === item));
    } catch { /* per-viewer convenience only */ }
    return defaultOn;
  });
  const [hover, setHover] = useState(null);
  const hasData = item => points.some(point => point[item.key] != null);
  const toggle = item => setChosen(current => {
    const next = current.includes(item.key) ? current.filter(k => k !== item.key) : [...current, item.key];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* per-viewer convenience only */ }
    return next;
  });
  const swatch = item => <i style={{ background: item.dashed ? 'transparent' : item.color, borderTop: item.dashed ? `2px dashed ${item.color}` : undefined }} />;
  return <div className='mcp mcp--halves'>
    {halves.map(half => <fieldset key={half.key} className='mcp__choice' aria-label={`Показатели: ${half.title}`}>
      <legend>{half.title}</legend>
      {half.series.map(item => <label key={item.key} className={hasData(item) ? '' : 'is-empty'}>
        <input type='checkbox' checked={chosen.includes(item.key)} disabled={!hasData(item)} onChange={() => toggle(item)} />
        {swatch(item)}{item.label}{hasData(item) ? '' : ' — нет данных'}</label>)}
    </fieldset>)}
    {halves.map((half, index) => {
      const shown = half.series.filter(item => chosen.includes(item.key) && hasData(item));
      const side = which => shown.find(item => item.side === which)?.key;
      const last = index === halves.length - 1;
      return <section key={half.key} className='mcp__half' aria-label={`График: ${half.title}`}>
        {shown.length === 0
          ? <p className='mc__note'>{half.title}: отметьте хотя бы один показатель.</p>
          : <MetricChart points={points} series={shown} ticks={ticks} left={64} right={64} height={last ? 230 : 190}
            axes={{ left: side('left'), right: side('right') }} showTicks={last} heading={half.title}
            hoverIndex={hover} onHover={setHover} />}
      </section>;
    })}
    {footer && <p className='mc__note'>{footer}</p>}
  </div>;
}
